import React, { useMemo, useState } from "react";
import about_pattern from "../../assests/about_pattern.svg";
import "./ContactUs.css";
import { submitContactFormCall } from "../../apis/contact-form/repo";
import { isInvalidEmail } from "../../utils/validations";
import { useLanguage } from "../../i18n/LanguageContext";

const mobileOk = (s) => /^[\d+\-()\s]{8,24}$/.test((s || "").trim());

function computeFieldErrors(s, errors) {
  const e = {};
  const n = s.name.trim();
  const c = s.companyName.trim();
  const em = s.email.trim();
  const m = s.mobile.trim();
  const sub = s.subject.trim();
  const msg = s.message.trim();
  if (!n) e.name = errors.nameRequired;
  else if (n.length > 200) e.name = errors.nameLong;
  if (!c) e.companyName = errors.companyRequired;
  else if (c.length > 200) e.companyName = errors.companyLong;
  if (!em) e.email = errors.emailRequired;
  else if (em.length > 320 || isInvalidEmail(em)) e.email = errors.emailInvalid;
  if (!m) e.mobile = errors.mobileRequired;
  else if (!mobileOk(m)) e.mobile = errors.mobileInvalid;
  if (!sub) e.subject = errors.subjectRequired;
  else if (sub.length > 500) e.subject = errors.subjectLong;
  if (!msg) e.message = errors.messageRequired;
  else if (msg.length > 8000) e.message = errors.messageLong;
  return e;
}

function ContactUs() {
  const { t, copy } = useLanguage();
  const [state, setState] = useState({
    name: "",
    companyName: "",
    email: "",
    mobile: "",
    subject: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  /** Field-level errors only after user tries to submit (avoids “all red” on first paint). */
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const fieldErrors = useMemo(
    () => (submitAttempted ? computeFieldErrors(state, copy.contact.errors) : {}),
    [state, submitAttempted, copy],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormError(null);
    setSubmitSuccess(false);
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setFormError(null);
    setSubmitSuccess(false);
    const errors = computeFieldErrors(state, copy.contact.errors);
    if (Object.keys(errors).length > 0) {
      setFormError(t("contact.fixFields"));
      return;
    }
    const payload = {
      name: state.name.trim(),
      companyName: state.companyName.trim(),
      email: state.email.trim(),
      mobile: state.mobile.trim(),
      subject: state.subject.trim(),
      message: state.message.trim(),
    };
    setIsLoading(true);
    try {
      await submitContactFormCall(payload);
      setState({
        name: "",
        companyName: "",
        email: "",
        mobile: "",
        subject: "",
        message: "",
      });
      setSubmitAttempted(false);
      setFormError(null);
      setSubmitSuccess(true);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.title ||
        err?.message ||
        t("contact.genericError");
      setFormError(typeof msg === "string" ? msg : t("contact.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const showField = (key) => Boolean(submitAttempted && fieldErrors[key]);

  return (
    <div
      id="contact_us"
      style={{
        backgroundImage: `url(${about_pattern})`,
        backgroundSize: "cover",
      }}
      className="w-full bg-[#1E1E1E] md:py-16"
    >
      <div className="contact-panel-shell w-full max-w-[720px] md:max-w-none md:w-1/2 m-auto bg-[#171717]">
        <form className="form p-[18px] md:p-[50px]" onSubmit={handleSubmit} noValidate>
          <p className="title">{t("contact.title")}</p>
          {submitSuccess ? (
            <div
              className="form-success"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <span className="form-success__check" aria-hidden="true" />
              <div className="form-success__text">
                <strong className="form-success__title">{t("contact.sentTitle")}</strong>
                <p className="form-success__body">{t("contact.sentBody")}</p>
              </div>
            </div>
          ) : null}
          {formError ? (
            <p className="form-error" role="alert">
              {formError}
            </p>
          ) : null}
          <div className="flex1">
            <label className="w-1/2 flex1-field contact-field">
              <input
                className="input1"
                type="text"
                name="name"
                placeholder=" "
                value={state.name}
                onChange={handleChange}
                autoComplete="name"
                aria-invalid={showField("name")}
              />
              <span>{t("contact.name")}</span>
              {showField("name") ? (
                <span className="field-error">{fieldErrors.name}</span>
              ) : null}
            </label>

            <label className="w-1/2 flex1-field contact-field">
              <input
                className="input1"
                type="text"
                name="companyName"
                placeholder=" "
                value={state.companyName}
                onChange={handleChange}
                autoComplete="organization"
                aria-invalid={showField("companyName")}
              />
              <span>{t("contact.company")}</span>
              {showField("companyName") ? (
                <span className="field-error">{fieldErrors.companyName}</span>
              ) : null}
            </label>
          </div>
          <div className="flex1">
            <label className="w-1/2 flex1-field contact-field">
              <input
                className="input1"
                type="email"
                name="email"
                placeholder=" "
                value={state.email}
                onChange={handleChange}
                autoComplete="email"
                aria-invalid={showField("email")}
              />
              <span>{t("contact.email")}</span>
              {showField("email") ? (
                <span className="field-error">{fieldErrors.email}</span>
              ) : null}
            </label>
            <label className="w-1/2 flex1-field contact-field">
              <input
                className="input1"
                type="tel"
                name="mobile"
                placeholder=" "
                value={state.mobile}
                onChange={handleChange}
                autoComplete="tel"
                aria-invalid={showField("mobile")}
              />
              <span>{t("contact.mobile")}</span>
              {showField("mobile") ? (
                <span className="field-error">{fieldErrors.mobile}</span>
              ) : null}
            </label>
          </div>
          <label className="contact-field">
            <input
              className="input1"
              type="text"
              name="subject"
              placeholder=" "
              value={state.subject}
              onChange={handleChange}
              aria-invalid={showField("subject")}
            />
            <span>{t("contact.subject")}</span>
            {showField("subject") ? (
              <span className="field-error">{fieldErrors.subject}</span>
            ) : null}
          </label>
          <label className="contact-field">
            <textarea
              style={{ resize: "none" }}
              className="input1"
              name="message"
              placeholder=" "
              value={state.message}
              onChange={handleChange}
              cols={30}
              rows={7}
              aria-invalid={showField("message")}
            />
            <span>{t("contact.message")}</span>
            {showField("message") ? (
              <span className="field-error">{fieldErrors.message}</span>
            ) : null}
          </label>
          <div className="w-full flex justify-end">
            <button className="submit" type="submit" disabled={isLoading}>
              {isLoading ? t("contact.processing") : t("contact.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactUs;
